import React, { useState, useEffect } from "react";
import { Button, Paper, Typography, Box, Fade, Zoom } from "@material-ui/core";
import { Home, ArrowBack, Search, Refresh } from "@material-ui/icons";
import { useNavigate } from "react-router-dom";

const NotFound = () => {
  const [animate, setAnimate] = useState(false);
  const [glitchActive, setGlitchActive] = useState(false);
  const navigate = useNavigate();

  // Mock navigation functions (replace with actual router)
  // const  = (path) => {
  //   console.log(`Navigating to: ${path}`);
  //   // Replace with actual navigation logic
  // };

  useEffect(() => {
    setAnimate(true);

    // Periodic glitch effect
    const glitchInterval = setInterval(() => {
      setGlitchActive(true);
      setTimeout(() => setGlitchActive(false), 200);
    }, 3000);

    return () => clearInterval(glitchInterval);
  }, []);

  const containerStyle = {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background:
      "linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)",
    backgroundSize: "400% 400%",
    animation: "gradientShift 8s ease infinite",
    padding: "20px",
    position: "relative",
    overflow: "hidden",
  };

  const paperStyle = {
    maxWidth: "600px",
    width: "100%",
    padding: "60px 40px",
    textAlign: "center",
    borderRadius: "24px",
    background: "rgba(255, 255, 255, 0.1)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    boxShadow: "0 25px 50px rgba(0, 0, 0, 0.15)",
    position: "relative",
    transform: animate ? "translateY(0)" : "translateY(50px)",
    transition: "all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)",
  };

  const glitchStyle = {
    fontSize: "8rem",
    fontWeight: "900",
    margin: "0",
    color: "#fff",
    textShadow: glitchActive
      ? "0.05em 0 0 #ff006e, -0.025em -0.05em 0 #00f5ff, 0.025em 0.05em 0 #ffbe0b"
      : "0 4px 20px rgba(0, 0, 0, 0.3)",
    animation: glitchActive ? "glitch 0.3s" : "float 3s ease-in-out infinite",
    transition: "all 0.3s ease",
  };

  const floatingElementStyle = (delay) => ({
    position: "absolute",
    width: "100px",
    height: "100px",
    borderRadius: "50%",
    background:
      "linear-gradient(45deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.3))",
    animation: `float 6s ease-in-out infinite ${delay}s`,
    zIndex: 0,
  });

  return (
    <div style={containerStyle}>
      <style jsx>{`
        @keyframes gradientShift {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }

        @keyframes float {
          0%,
          100% {
            transform: translateY(0) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(180deg);
          }
        }

        @keyframes glitch {
          0% {
            transform: translate(0);
          }
          20% {
            transform: translate(-2px, 2px);
          }
          40% {
            transform: translate(-2px, -2px);
          }
          60% {
            transform: translate(2px, 2px);
          }
          80% {
            transform: translate(2px, -2px);
          }
          100% {
            transform: translate(0);
          }
        }

        @keyframes pulse {
          0%,
          100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }

        .gradient-text {
          background: linear-gradient(45deg, #ff006e, #8338ec, #3a86ff);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: gradientShift 3s ease infinite;
        }

        .hover-button {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          transform: translateY(0);
        }

        .hover-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
        }
      `}</style>

      {/* Floating background elements */}
      <div style={{ ...floatingElementStyle(0), top: "10%", left: "10%" }} />
      <div style={{ ...floatingElementStyle(1), top: "20%", right: "15%" }} />
      <div style={{ ...floatingElementStyle(2), bottom: "15%", left: "20%" }} />
      <div
        style={{ ...floatingElementStyle(1.5), bottom: "25%", right: "10%" }}
      />

      <Fade in={animate} timeout={1000}>
        <Paper elevation={0} style={paperStyle}>
          <Zoom in={animate} timeout={1200}>
            <Box>
              <Typography style={glitchStyle} component="h1">
                404
              </Typography>
            </Box>
          </Zoom>

          <Fade in={animate} timeout={1500}>
            <Box>
              <Typography
                variant="h3"
                component="h2"
                className="gradient-text"
                style={{
                  margin: "20px 0 10px",
                  fontWeight: "700",
                  fontSize: "2.5rem",
                }}
              >
                Oops! Lost in Space
              </Typography>

              <Typography
                variant="h6"
                style={{
                  color: "rgba(255, 255, 255, 0.8)",
                  marginBottom: "40px",
                  fontWeight: "300",
                  lineHeight: 1.6,
                }}
              >
                The page you're looking for has drifted into the digital void.
                <br />
                Let's navigate you back to familiar territory.
              </Typography>
            </Box>
          </Fade>

          <Fade in={animate} timeout={2000}>
            <Box
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "15px",
                alignItems: "center",
              }}
            >
              <Box
                style={{
                  display: "flex",
                  gap: "15px",
                  flexWrap: "wrap",
                  justifyContent: "center",
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<Home />}
                  onClick={() => navigate("/dashboard")}
                  className="hover-button"
                  style={{
                    borderRadius: "50px",
                    padding: "12px 30px",
                    background: "linear-gradient(45deg, #667eea, #764ba2)",
                    border: "none",
                    fontWeight: "600",
                    textTransform: "none",
                    fontSize: "1rem",
                  }}
                >
                  Dashboard
                </Button>

                <Button
                  variant="outlined"
                  color="secondary"
                  size="large"
                  startIcon={<ArrowBack />}
                  onClick={() => navigate("/")}
                  className="hover-button"
                  style={{
                    borderRadius: "50px",
                    padding: "12px 30px",
                    borderColor: "rgba(255, 255, 255, 0.5)",
                    color: "#fff",
                    fontWeight: "600",
                    textTransform: "none",
                    fontSize: "1rem",
                  }}
                >
                  Go Back
                </Button>
              </Box>

              <Box style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
                <Button
                  variant="text"
                  startIcon={<Search />}
                  onClick={() => navigate("/search")}
                  style={{
                    color: "rgba(255, 255, 255, 0.7)",
                    textTransform: "none",
                  }}
                >
                  Search
                </Button>

                <Button
                  variant="text"
                  startIcon={<Refresh />}
                  onClick={() => window.location.reload()}
                  style={{
                    color: "rgba(255, 255, 255, 0.7)",
                    textTransform: "none",
                  }}
                >
                  Refresh
                </Button>
              </Box>
            </Box>
          </Fade>

          {/* Decorative elements */}
          <Box
            style={{
              position: "absolute",
              top: "-50px",
              right: "-50px",
              width: "100px",
              height: "100px",
              borderRadius: "50%",
              background:
                "linear-gradient(45deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.3))",
              animation: "pulse 2s ease-in-out infinite",
            }}
          />

          <Box
            style={{
              position: "absolute",
              bottom: "-30px",
              left: "-30px",
              width: "60px",
              height: "60px",
              borderRadius: "50%",
              background:
                "linear-gradient(45deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.2))",
              animation: "pulse 2s ease-in-out infinite 0.5s",
            }}
          />
        </Paper>
      </Fade>
    </div>
  );
};

export default NotFound;
